<template>
  <div class="register-page">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="mesh-overlay"></div>
    </div>

    <!-- Register Container -->
    <div class="register-container">
      <!-- Branding Section -->
      <div class="branding-section">
        <div class="brand-content">
          <div class="logo-icon">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24 4L44 14V34L24 44L4 34V14L24 4Z"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
              <path
                d="M24 16V32M16 24H32"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h1 class="brand-title">Central de Saúde Digital</h1>
          <p class="brand-subtitle">Cadastro de Administradores</p>

          <div class="features-list">
            <div class="feature-item">
              <span class="feature-icon">✉️</span>
              <span>Receba um convite por e-mail</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">🔐</span>
              <span>Acesso seguro à plataforma</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">👨‍💼</span>
              <span>Gerencie sua unidade de saúde</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Register Form Section -->
      <div class="form-section">
        <div class="register-card">
          <!-- Success State -->
          <div v-if="success" class="success-state">
            <div class="success-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2>Cadastro Realizado!</h2>
            <p>
              Um convite foi enviado para <strong>{{ form.email }}</strong>
            </p>
            <p class="success-hint">
              Verifique sua caixa de entrada e siga as instruções para ativar
              sua conta.
            </p>
            <router-link to="/" class="back-link">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Voltar para o Login
            </router-link>
          </div>

          <!-- Form State -->
          <template v-else>
            <div class="card-header">
              <h2>Solicitar Acesso</h2>
              <p>Preencha seus dados para receber um convite</p>
            </div>

            <form @submit.prevent="handleSubmit" class="register-form">
              <div class="form-row">
                <div class="input-group">
                  <label for="firstName">Nome</label>
                  <div class="input-wrapper">
                    <span class="input-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      id="firstName"
                      v-model="form.firstName"
                      type="text"
                      placeholder="Seu nome"
                      required
                      autocomplete="given-name"
                    />
                  </div>
                </div>

                <div class="input-group">
                  <label for="lastName">Sobrenome</label>
                  <div class="input-wrapper">
                    <span class="input-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      id="lastName"
                      v-model="form.lastName"
                      type="text"
                      placeholder="Seu sobrenome"
                      required
                      autocomplete="family-name"
                    />
                  </div>
                </div>
              </div>

              <div class="input-group">
                <label for="email">E-mail Corporativo</label>
                <div class="input-wrapper" :class="{ 'has-error': emailError }">
                  <span class="input-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    :placeholder="'exemplo' + ALLOWED_DOMAINS[0]"
                    required
                    autocomplete="email"
                    @blur="validateEmail"
                  />
                </div>
                <span v-if="emailError" class="field-error">{{
                  emailError
                }}</span>
                <span class="field-hint"
                  >Apenas e-mails {{ ALLOWED_DOMAINS.join(' ou ') }} são
                  aceitos</span
                >
              </div>

              <div class="input-group">
                <label for="gender">Sexo</label>
                <div class="input-wrapper select-wrapper">
                  <span class="input-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v8M8 12h8" />
                    </svg>
                  </span>
                  <select id="gender" v-model="form.gender" required>
                    <option value="" disabled>Selecione</option>
                    <option value="Male">Masculino</option>
                    <option value="Female">Feminino</option>
                  </select>
                  <span class="select-arrow">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="loading || !isFormValid"
              >
                <span v-if="loading" class="loading-spinner"></span>
                <span>{{ loading ? "Enviando..." : "Solicitar Acesso" }}</span>
                <svg
                  v-if="!loading"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>

              <p v-if="error" class="error-msg">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {{ error }}
              </p>
            </form>

            <footer class="card-footer">
              <router-link to="/" class="back-link">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Voltar para o Login
              </router-link>
            </footer>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  inviteGeneralAdmin,
  isValidDomain,
  ALLOWED_DOMAINS,
} from "@/services/inviteService";
import { logger } from "@/utils/logger";

export default {
  name: "RegisterPage",
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        email: "",
        gender: "",
      },
      loading: false,
      error: null,
      emailError: null,
      success: false,
      ALLOWED_DOMAINS,
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.firstName.trim() &&
        this.form.lastName.trim() &&
        this.form.email.trim() &&
        this.form.gender &&
        !this.emailError &&
        isValidDomain(this.form.email)
      );
    },
  },
  methods: {
    validateEmail() {
      if (!this.form.email) {
        this.emailError = null;
        return;
      }

      if (!isValidDomain(this.form.email)) {
        this.emailError = `O e-mail deve pertencer a um dos domínios: ${ALLOWED_DOMAINS.join(" ou ")}`;
      } else {
        this.emailError = null;
      }
    },
    async handleSubmit() {
      this.validateEmail();
      if (this.emailError) return;

      this.loading = true;
      this.error = null;

      try {
        await inviteGeneralAdmin({
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          gender: this.form.gender,
        });

        this.success = true;
      } catch (err) {
        logger.error("Erro ao enviar convite", err);
        this.error =
          "O cadastro automático ainda não está disponível. Entre em contato com a.garbiati@portaltelemedicina.com.br para solicitar acesso.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   REGISTER PAGE - Modern Healthcare Admin
   ======================================== */

.register-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
}

/* ========================================
   ANIMATED BACKGROUND
   ======================================== */
.animated-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
  bottom: -150px;
  right: -150px;
  animation-delay: -7s;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

.mesh-overlay {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.02) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 75% 75%,
      rgba(255, 255, 255, 0.02) 0%,
      transparent 50%
    );
  backdrop-filter: blur(100px);
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(50px, -50px) scale(1.1);
  }
  50% {
    transform: translate(0, 50px) scale(0.95);
  }
  75% {
    transform: translate(-50px, -25px) scale(1.05);
  }
}

/* ========================================
   REGISTER CONTAINER
   ======================================== */
.register-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 1100px;
  width: 95%;
  min-height: 650px;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

/* ========================================
   BRANDING SECTION
   ======================================== */
.branding-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1) 0%,
    rgba(139, 92, 246, 0.1) 100%
  );
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}

.brand-content {
  text-align: center;
  max-width: 380px;
}

.logo-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 20px;
  color: white;
  box-shadow: 0 10px 40px rgba(59, 130, 246, 0.3);
}

.logo-icon svg {
  width: 100%;
  height: 100%;
}

.brand-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
}

.brand-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2.5rem;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.feature-icon {
  font-size: 1.25rem;
}

/* ========================================
   FORM SECTION
   ======================================== */
.form-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.register-card {
  width: 100%;
  max-width: 420px;
}

.card-header {
  margin-bottom: 2rem;
  text-align: center;
}

.card-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
}

.card-header p {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
}

/* ========================================
   FORM STYLES
   ======================================== */
.register-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.has-error input,
.input-wrapper.has-error select {
  border-color: #ef4444;
}

.input-icon {
  position: absolute;
  left: 1rem;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  pointer-events: none;
}

.input-wrapper input,
.input-wrapper select {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #fff;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.input-wrapper input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.input-wrapper input:focus,
.input-wrapper select:focus {
  outline: none;
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.select-wrapper {
  position: relative;
}

.select-wrapper select {
  appearance: none;
  cursor: pointer;
}

.select-wrapper select option {
  background: #1e293b;
  color: #fff;
}

.select-arrow {
  position: absolute;
  right: 1rem;
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
}

.field-error {
  font-size: 0.8rem;
  color: #ef4444;
}

.field-hint {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
}

/* ========================================
   SUBMIT BUTTON
   ======================================== */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem;
  margin-top: 0.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========================================
   ERROR MESSAGE
   ======================================== */
.error-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #fca5a5;
  font-size: 0.875rem;
}

/* ========================================
   SUCCESS STATE
   ======================================== */
.success-state {
  text-align: center;
  padding: 2rem 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  color: white;
  box-shadow: 0 10px 40px rgba(16, 185, 129, 0.3);
}

.success-icon svg {
  width: 100%;
  height: 100%;
}

.success-state h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}

.success-state p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.5rem;
}

.success-hint {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5) !important;
  margin-bottom: 2rem !important;
}

/* ========================================
   FOOTER
   ======================================== */
.card-footer {
  margin-top: 2rem;
  text-align: center;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s ease;
}

.back-link:hover {
  color: #3b82f6;
}

/* ========================================
   RESPONSIVE
   ======================================== */
@media (max-width: 900px) {
  .register-container {
    grid-template-columns: 1fr;
    max-width: 500px;
    min-height: auto;
  }

  .branding-section {
    display: none;
  }

  .form-section {
    padding: 2rem;
  }
}

@media (max-width: 500px) {
  .register-container {
    width: 100%;
    min-height: 100vh;
    border-radius: 0;
    border: none;
  }

  .form-section {
    padding: 1.5rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
